import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { FlaskConical, ShoppingCart, Copy, Check, X, Plus, Minus, Loader2, Package, AlertTriangle, Sparkles, CreditCard, Coins } from "lucide-react";

export default function Peptides() {
  const [catalog, setCatalog] = useState([]);
  const [rate, setRate] = useState(110);
  const [wallet, setWallet] = useState("");
  const [cart, setCart] = useState({});  // { peptide_id: qty }
  const [checkout, setCheckout] = useState(false);
  const [order, setOrder] = useState(null);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    api.get("/peptides/catalog").then((r) => {
      setCatalog(r.data.products);
      setRate(r.data.gbp_per_sol);
      setWallet(r.data.wallet_address);
    });
    api.get("/peptides/orders/me").then((r) => setOrders(r.data));
  }, []);

  const cartItems = catalog.filter((p) => cart[p.id] > 0);
  const subtotalGbp = cartItems.reduce((s, p) => s + p.price_gbp * cart[p.id], 0);
  const subtotalSol = subtotalGbp / rate;
  const inCart = cartItems.length;

  const add = (id) => setCart((c) => ({ ...c, [id]: (c[id] || 0) + 1 }));
  const dec = (id) => setCart((c) => {
    const n = (c[id] || 0) - 1;
    if (n <= 0) { const { [id]: _, ...rest } = c; return rest; }
    return { ...c, [id]: n };
  });

  const categories = [...new Set(catalog.map((p) => p.category))];

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-10 relative z-10">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <span className="tape-label">RESEARCH SUPPLY</span>
          <h1 className="font-display text-5xl md:text-7xl mt-3 leading-none">
            <span className="text-[var(--primary)] glow">PEPTIDES.</span> DIRECT.
          </h1>
          <p className="text-[var(--text-muted)] mt-4 max-w-xl text-sm">
            Research-grade peptides shipped worldwide. Pay in SOL. Order tracked to your Ascend account.
          </p>
        </div>
        <div className="text-right">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">£1 ≈</div>
          <div className="font-mono text-sm text-[var(--primary)]">{(1 / rate).toFixed(5)} SOL</div>
        </div>
      </div>

      <div className="panel-bright p-4 mb-6 flex items-start gap-3 border-l-4 border-[var(--warning)]">
        <AlertTriangle className="w-5 h-5 text-[var(--warning)] shrink-0 mt-0.5" />
        <div className="text-xs font-mono text-[var(--text-muted)] leading-relaxed">
          <span className="text-white">FOR RESEARCH USE ONLY.</span> Not for human consumption. You are 21+ and understand
          local regulations. Ascend does not provide medical advice — always consult a licensed physician.
        </div>
      </div>

      {/* Catalog */}
      {categories.map((cat) => (
        <div key={cat} className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <FlaskConical className="w-4 h-4 text-[var(--primary)]" />
            <span className="tape-label">{cat.toUpperCase()}</span>
          </div>
          <div className="grid grid-cols-12 gap-px bg-[var(--border)]">
            {catalog.filter((p) => p.category === cat).map((p) => (
              <div key={p.id} className="col-span-12 md:col-span-6 lg:col-span-4 bg-[var(--bg)] p-5" data-testid={`peptide-${p.id}`}>
                <div className="flex items-baseline justify-between mb-2">
                  <div className="font-display text-2xl">{p.name}</div>
                  <div className="font-display text-xl text-[var(--primary)]">£{p.price_gbp}</div>
                </div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-3">
                  {p.vial} vial · ≈ {(p.price_gbp / rate).toFixed(4)} SOL
                </div>
                <p className="text-sm text-[var(--text-muted)] mb-4">{p.summary}</p>
                {cart[p.id] ? (
                  <div className="flex items-center justify-between gap-2">
                    <button onClick={() => dec(p.id)} className="btn-ghost px-3 py-2" data-testid={`peptide-${p.id}-minus`}>
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="font-display text-2xl text-[var(--primary)]" data-testid={`peptide-${p.id}-qty`}>{cart[p.id]}</span>
                    <button onClick={() => add(p.id)} className="btn-ghost px-3 py-2" data-testid={`peptide-${p.id}-plus`}>
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <button onClick={() => add(p.id)} className="btn-brutal w-full" data-testid={`peptide-${p.id}-add`}>
                    <ShoppingCart className="inline w-4 h-4 mr-2" /> Add to cart
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* My orders */}
      {orders.length > 0 && (
        <div className="mt-10">
          <span className="tape-label">MY ORDERS</span>
          <div className="mt-4 space-y-px bg-[var(--border)]">
            {orders.map((o) => (
              <div key={o.id} className="bg-[var(--surface)] p-4 grid grid-cols-12 gap-4 items-center" data-testid={`my-order-${o.id}`}>
                <div className="col-span-3">
                  <div className="font-mono text-white text-sm">{o.order_number || o.id.slice(0, 8)}</div>
                  <div className="text-[10px] font-mono text-[var(--text-muted)]">{new Date(o.created_at).toLocaleDateString()}</div>
                </div>
                <div className="col-span-4 text-xs font-mono text-[var(--text-muted)] truncate">
                  {o.items.map((i) => `${i.qty}× ${i.name}`).join(", ")}
                </div>
                <div className="col-span-2 font-mono text-sm text-white">£{o.subtotal_gbp}</div>
                <div className="col-span-3 text-right">
                  <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 border ${
                    o.status === "paid" ? "text-[var(--primary)] border-[var(--primary)]" :
                    o.status === "shipped" ? "text-[var(--success)] border-[var(--success)]" :
                    "text-[var(--warning)] border-[var(--warning)]"
                  }`}>
                    {o.status.toUpperCase()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sticky cart bar */}
      {inCart > 0 && !checkout && !order && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 panel-bright px-6 py-3 flex items-center gap-4 shadow-2xl" data-testid="cart-bar">
          <ShoppingCart className="w-5 h-5 text-[var(--primary)]" />
          <div>
            <div className="font-display text-lg">{inCart} item{inCart > 1 ? "s" : ""}</div>
            <div className="text-[10px] font-mono text-[var(--text-muted)]">£{subtotalGbp.toFixed(2)} · ≈ {subtotalSol.toFixed(4)} SOL</div>
          </div>
          <button onClick={() => setCheckout(true)} className="btn-brutal" data-testid="cart-checkout">
            Checkout →
          </button>
        </div>
      )}

      {checkout && !order && (
        <CheckoutModal
          cart={cart}
          catalog={catalog}
          subtotalGbp={subtotalGbp}
          onClose={() => setCheckout(false)}
          onOrderCreated={(o) => {
            setOrder(o);
            setCheckout(false);
            api.get("/peptides/orders/me").then((r) => setOrders(r.data));
          }}
        />
      )}

      {order && (
        <PaymentModal
          order={order}
          wallet={wallet}
          onClose={() => setOrder(null)}
          onPaid={() => {
            setOrder(null);
            setCart({});
            api.get("/peptides/orders/me").then((r) => setOrders(r.data));
          }}
        />
      )}
    </div>
  );
}

function CheckoutModal({ cart, catalog, subtotalGbp, onClose, onOrderCreated }) {
  const [shipping, setShipping] = useState({
    full_name: "", email: "", phone: "", line1: "", line2: "",
    city: "", region: "", postcode: "", country: "United Kingdom",
  });
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(null); // "sol" | "card" | null

  const validate = () => {
    if (!shipping.full_name.trim() || !shipping.email.trim() || !shipping.line1.trim()
        || !shipping.city.trim() || !shipping.postcode.trim() || !shipping.country.trim()) {
      toast.error("Please fill in the required shipping fields");
      return false;
    }
    return true;
  };

  const items = () => Object.entries(cart).filter(([, q]) => q > 0).map(([peptide_id, qty]) => ({ peptide_id, qty }));

  const payWithSol = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setBusy("sol");
    try {
      const { data } = await api.post("/peptides/order", { items: items(), shipping, notes });
      onOrderCreated(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Order failed");
    } finally { setBusy(null); }
  };

  const payWithCard = async () => {
    if (!validate()) return;
    setBusy("card");
    try {
      const { data } = await api.post("/payments/peptides/checkout", {
        items: items(), shipping, notes, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start card checkout");
      setBusy(null);
    }
  };

  const setF = (k, v) => setShipping((s) => ({ ...s, [k]: v }));

  const cartItems = catalog.filter((p) => cart[p.id] > 0);

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form onSubmit={payWithSol} onClick={(e) => e.stopPropagation()} className="panel-bright max-w-3xl w-full my-8" data-testid="checkout-modal">
        <div className="sticky top-0 bg-[var(--surface-2)] z-10 px-6 py-3 border-b border-[var(--border-bright)] flex justify-between items-center">
          <span className="tape-label">CHECKOUT · £{subtotalGbp.toFixed(2)}</span>
          <button type="button" onClick={onClose} className="text-[var(--text-muted)]" data-testid="checkout-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div>
            <span className="tape-label">CART</span>
            <div className="mt-3 space-y-1">
              {cartItems.map((p) => (
                <div key={p.id} className="flex justify-between text-sm font-mono">
                  <span>{cart[p.id]}× {p.name}</span>
                  <span>£{(p.price_gbp * cart[p.id]).toFixed(2)}</span>
                </div>
              ))}
              <div className="flex justify-between text-sm font-mono border-t border-[var(--border)] pt-2 mt-2 text-white">
                <span>Subtotal</span><span>£{subtotalGbp.toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div>
            <span className="tape-label">SHIPPING ADDRESS</span>
            <div className="mt-3 grid grid-cols-12 gap-3">
              <Input col="6" label="Full name" v={shipping.full_name} on={(v) => setF("full_name", v)} required test="ship-name" />
              <Input col="6" label="Email" v={shipping.email} on={(v) => setF("email", v)} type="email" required test="ship-email" />
              <Input col="6" label="Phone" v={shipping.phone} on={(v) => setF("phone", v)} test="ship-phone" />
              <Input col="6" label="Country" v={shipping.country} on={(v) => setF("country", v)} required test="ship-country" />
              <Input col="12" label="Address line 1" v={shipping.line1} on={(v) => setF("line1", v)} required test="ship-line1" />
              <Input col="12" label="Address line 2 (optional)" v={shipping.line2} on={(v) => setF("line2", v)} test="ship-line2" />
              <Input col="5" label="City" v={shipping.city} on={(v) => setF("city", v)} required test="ship-city" />
              <Input col="4" label="Region / State" v={shipping.region} on={(v) => setF("region", v)} test="ship-region" />
              <Input col="3" label="Postcode" v={shipping.postcode} on={(v) => setF("postcode", v)} required test="ship-postcode" />
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-2">Order notes (optional)</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} maxLength={500} className="brutal" data-testid="ship-notes" />
          </div>

          <div>
            <span className="tape-label">CHOOSE PAYMENT METHOD</span>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
              <button
                type="button"
                onClick={payWithCard}
                disabled={!!busy}
                className="btn-brutal w-full flex items-center justify-center gap-2"
                data-testid="checkout-pay-card"
              >
                {busy === "card" ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting to Stripe…</>
                ) : (
                  <><CreditCard className="w-4 h-4" /> Pay £{subtotalGbp.toFixed(2)} by card</>
                )}
              </button>
              <button
                type="submit"
                disabled={!!busy}
                className="btn-ghost w-full flex items-center justify-center gap-2"
                data-testid="checkout-submit"
              >
                {busy === "sol" ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Generating order…</>
                ) : (
                  <><Coins className="w-4 h-4" /> Pay with SOL instead</>
                )}
              </button>
            </div>
            <p className="mt-3 text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] text-center">
              Card: instant, receipt via email. SOL: no third-party processor.
            </p>
          </div>
        </div>
      </form>
    </div>
  );
}

const Input = ({ col, label, v, on, type = "text", required, test }) => (
  <div className={`col-span-12 md:col-span-${col}`}>
    <label className="block text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-2">{label}</label>
    <input type={type} value={v} required={required} onChange={(e) => on(e.target.value)} className="brutal" data-testid={test} />
  </div>
);

function PaymentModal({ order, wallet, onClose, onPaid }) {
  const [signature, setSignature] = useState("");
  const [busy, setBusy] = useState(false);
  const [orderNumber, setOrderNumber] = useState(null);
  const [copied, setCopied] = useState(null);

  const copy = (v, k) => { navigator.clipboard.writeText(v); setCopied(k); toast.success("Copied."); setTimeout(() => setCopied(null), 1200); };

  const verify = async () => {
    const sig = signature.trim();
    if (!sig) { toast.error("Paste your tx signature"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/peptides/order/${order.order_id}/verify`, { order_id: order.order_id, signature: sig });
      setOrderNumber(data.order_number);
      toast.success(`Order confirmed: ${data.order_number}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verification failed");
    } finally { setBusy(false); }
  };

  if (orderNumber) {
    return (
      <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onPaid}>
        <div onClick={(e) => e.stopPropagation()} className="panel-bright max-w-lg w-full p-8 text-center" data-testid="order-confirmed">
          <Sparkles className="w-12 h-12 text-[var(--primary)] mx-auto mb-4 glow" />
          <div className="tape-label mx-auto inline-block mb-3">ORDER CONFIRMED</div>
          <div className="font-display text-5xl text-[var(--primary)] glow mb-2" data-testid="order-number">{orderNumber}</div>
          <p className="text-sm text-[var(--text-muted)] mb-6">
            Your order is being processed and will ship within 2-5 business days. Track it in "MY ORDERS".
          </p>
          <button onClick={onPaid} className="btn-brutal w-full" data-testid="order-confirmed-close">Done</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="panel-bright max-w-2xl w-full my-8" data-testid="payment-modal">
        <div className="px-6 py-3 border-b border-[var(--border-bright)] flex justify-between items-center">
          <span className="tape-label">PAYMENT · £{order.subtotal_gbp} ≈ {order.amount_sol} SOL</span>
          <button onClick={onClose} className="text-[var(--text-muted)]" data-testid="payment-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-2">Send EXACTLY</label>
            <div className="flex items-center gap-2">
              <code className="bg-[var(--surface)] border border-[var(--border-bright)] px-3 py-2 font-mono text-lg text-[var(--primary)] flex-1">{order.amount_sol} SOL</code>
              <button onClick={() => copy(String(order.amount_sol), "amt")} className="btn-ghost text-xs px-3" data-testid="pay-copy-amount">
                {copied === "amt" ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-2">To wallet</label>
            <div className="flex items-center gap-2">
              <code className="bg-[var(--surface)] border border-[var(--border-bright)] px-3 py-2 font-mono text-xs text-white break-all flex-1">{wallet}</code>
              <button onClick={() => copy(wallet, "w")} className="btn-ghost text-xs px-3" data-testid="pay-copy-wallet">
                {copied === "w" ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-2">Transaction signature</label>
            <textarea value={signature} onChange={(e) => setSignature(e.target.value)} rows={4} placeholder="Paste the tx signature from your Solana wallet…" className="brutal font-mono text-xs" data-testid="pay-signature" />
          </div>
          <button onClick={verify} disabled={busy || !signature.trim()} className="btn-brutal w-full" data-testid="pay-verify">
            {busy ? <span className="flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Verifying on-chain…</span> : "Verify & Confirm Order →"}
          </button>
          <p className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">
            Send the exact amount shown. A random cent-suffix identifies your order.
          </p>
        </div>
      </div>
    </div>
  );
}
